import React, { Fragment, useMemo, useEffect, } from 'react';
import { components as reactSelectComponents } from 'react-select';
import { set } from 'lodash/fp';
import { Input, } from 'reactstrap';
import { last, pickBy, uniq, mapValues, groupBy, sumBy, omitBy, isUndefined, isEqual, isEmpty, fromPairs, pick, keyBy, sortBy, orderBy, } from 'lodash';
import { collection, collectionGroup, query, where, getCountFromServer, } from 'firebase/firestore';
import { useAsync, useList } from 'react-use';
import { toast } from 'react-toastify';
import numeral from 'numeral';
import classnames from 'classnames';
import { format as formatDate, addMonths, eachMonthOfInterval, addYears, startOfMonth, endOfMonth, addDays, getDaysInMonth, getDate, } from 'date-fns';
import qs from 'qs';

import firebase, { functions } from '../../firebase';
import { batch, } from '../../shared/firebase';
import { canUpdateProspectPattern, } from '../../shared/abilities';
import AdminPage from '../hocs/AdminPage';
import ModelFormModal from '../modals/ModelFormModal';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useFirebaseUser from '../hooks/useFirebaseUser';
import QueryDateRangeSelector from '../QueryDateRangeSelector';
import AdminHelp from '../AdminHelp';
import InventoriesHeader from '../InventoriesHeader';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ProgressButton from '../ProgressButton';
import QuerySelector from '../QuerySelector';
import { prospectFields, } from '../../shared/models/product';
import { computeSchedule, } from '../../shared/models/purchaseOrder';
import useQueryParams from '../hooks/useQueryParams';
import useSimulation from '../hooks/useSimulation';
import QueryBoolean from '../QueryBoolean';
import QueryButtonGroup from '../QueryButtonGroup';
import ModalButton from '../ModalButton';
import TenantLink from '../TenantLink';
import SavedSearchParameterForm from '../forms/SavedSearchParameterForm';

const { keys, entries } = Object;
const commentFields = {
  body: {
    type: 'text',
    label: 'コメント内容',
    validations: {
      required: _ => !isEmpty(_),
    },
  },
};
const metrics = {
  currentYearOrderCount: {
    label: '注文数',
  },
  prevYearOrderCount: {
    label: '前年注文数',
  },
  predictedOrderCount: {
    label: '予測注文数',
  },
  vsPrevYearOrderCount: {
    label: 'VS前年',
    isComparison: _ => true,
    rate: _ => _.vsPrevYearOrderCount() / _.prevYearOrderCount(),
  },
  expectedOrderCount: {
    label: '予想販売数',
  },
  arrivalCount: {
    label: '入荷予定数',
    path: _ => (_.type !== 'product' && _.groupMode === 'group') ? null : `/admin/inventories/containers?${qs.stringify({ [_.type === 'product' ? 'productIds' : 'productTypeIds']: [_.id], arrivalDateRange: [formatDate(_.month, 'yyyy-MM-dd'), formatDate(endOfMonth(_.month), 'yyyy-MM-dd')], })}`,
  },
  tmpOrderCount: {
    label: '卸仮注文数',
  },
  inventoryCount: {
    label: '在庫数',
    className: _ => _.shouldAlertInventory?.() ? (_.isPast ? 'bg-danger text-white' : 'bg-danger') : '',
    style: _ => !_.isPast ? { color: '#28a745' } : {},
  },
  preOrderCount: {
    label: '予約注文数',
  },
  preOrderRemaining: {
    label: '予約注文残',
  },
  completedCount: {
    label: '完成数',
    path: _ => (_.type !== 'product' && _.groupMode === 'group') ? null : `/admin/inventories/purchaseOrders?${qs.stringify({ [_.type === 'product' ? 'productIds' : 'productTypeIds']: [_.id], completedOnRange: [formatDate(_.month, 'yyyy-MM-dd'), formatDate(endOfMonth(_.month), 'yyyy-MM-dd')], })}`,
  },
  transferingCount: {
    label: '輸送中',
    path: _ => (_.type !== 'product' && _.groupMode === 'group') ? null : `/admin/inventories/containers?${qs.stringify({
      [_.type === 'product' ? 'productIds' : 'productTypeIds']: [_.id],
      departureDateRange: ['1900-01-01', formatDate(endOfMonth(_.month), 'yyyy-MM-dd')],
      arrivalDateRange: [formatDate(addMonths(_.month, 1), 'yyyy-MM-dd')],
    })}`,
  },
  abroadInventoryCount: {
    label: '海外在庫数',
    className: _ => _.shouldAlertAbroadInventory?.() ? (_.isPast ? 'bg-danger text-white' : 'bg-danger') : '',
    style: _ => !_.isPast ? { color: '#28a745' } : {},
  },
  totalInventoryCount: {
    label: '合計在庫数',
    className: _ => _.totalInventoryCount() < 0 ? (_.isPast ? 'bg-danger text-white' : 'bg-danger') : '',
    style: _ => !_.isPast ? { color: '#28a745' } : {},
  },
};
const metricOptions = entries(metrics).map(([k, v]) => ({ label: v.label, value: k }));
const defaultMetricNames = ['currentYearOrderCount', 'prevYearOrderCount', 'vsPrevYearOrderCount', 'expectedOrderCount', 'arrivalCount', 'inventoryCount', 'preOrderCount', 'preOrderRemaining', 'completedCount', 'transferingCount', 'abroadInventoryCount', 'totalInventoryCount'];
const productKinds = {
  body: { label: '本体', field: 'isBody', },
  option: { label: 'オプション', field: 'isOption', },
  part: { label: 'パーツ', field: 'isPart', },
};
const productKindOptions = entries(productKinds).map(([k, v]) => ({ label: v.label, value: k }));
const ungroupedGroupingId = '__ungrouped__';

const db = firebase.firestore();
const saveOrderCounts = functions.httpsCallable('saveOrderCounts');

export default AdminPage(function AdminInventoriesSchedule (props) {
  const { history, location, tenant, user } = props;
  const queryParams = useQueryParams();
  const { firebaseUser } = useFirebaseUser();
  const { isSimulating, isEditable, isMasterLocked, withChanges, } = useSimulation();
  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedId, }] = useList([]);
  const unselectId = _ => removeSelectedIdAt(selectedIds.indexOf(_));
  const start = useMemo(_ => queryParams.dateRange?.[0] ? startOfMonth(new Date(queryParams.dateRange[0])) : startOfMonth(addMonths(new Date(), -3)), [queryParams.dateRange?.[0]]);
  const end = useMemo(_ => queryParams.dateRange?.[1] ? endOfMonth(new Date(queryParams.dateRange[1])) : endOfMonth(addMonths(start, 9)), [start, queryParams.dateRange?.[1]]);
  const productKind = productKinds[queryParams.productKind] ? queryParams.productKind : 'body';
  const isBodyKind = productKind === 'body';
  // 本体はベンチマーク商品の選択肢としても使うため常に購読する
  const bodyProducts = useCollectionSubscriptionInTenant(db.collection('products').where('isBody', '==', true));
  const optionOrPartProducts = useCollectionSubscriptionInTenant(!isBodyKind && db.collection('products').where(productKinds[productKind].field, '==', true), [productKind]);
  const targetProducts = isBodyKind ? bodyProducts : optionOrPartProducts;
  const months = useMemo(_ => eachMonthOfInterval({ start, end, }), [start, end]);
  const prospectPatterns = sortBy(useCollectionSubscriptionInTenant(db.collection('prospectPatterns'), []), _ => _.createdAt.toDate());
  const prospectPatternOptions = prospectPatterns.map(_ => ({ label: _.name, value: _.id }));
  const prospectPatternsById = useMemo(_ => keyBy(prospectPatterns, 'id'), [prospectPatterns]);
  const currentProspectPattern = prospectPatternsById[queryParams.prospectPatternId];
  const cellNotes = useCollectionSubscriptionInTenant(db.collection('cellNotes'));
  const cellNotesById = useMemo(_ => keyBy(cellNotes, 'id'), [cellNotes]);
  const isActual = queryParams.isActual === '1';
  const _purchaseOrders = useCollectionSubscriptionInTenant(db.collection('purchaseOrders'));
  const _containers = useCollectionSubscriptionInTenant(db.collection('containers'));
  // シミュレーション中は変更内容を重ねたうえで在庫を計算する
  const purchaseOrders = useMemo(_ => withChanges(_purchaseOrders, 'purchaseOrders'), [_purchaseOrders, withChanges]);
  const containers = useMemo(_ => withChanges(_containers, 'containers'), [_containers, withChanges]);
  const computedPurchaseAndContainerCounts = useMemo(_ => computeSchedule(purchaseOrders, containers, { isActual }), [purchaseOrders, containers, isActual]);
  const currentMonthOrders = useCollectionSubscriptionInTenant(db.collection('orders').where('createdAt', '>=', startOfMonth(new Date())));
  const currentMonthOrderItemsByProductId = useMemo(_ => groupBy(currentMonthOrders.flatMap(_ => _.orderItems), 'productId'), [currentMonthOrders]);
  const currentYearOrderCounts = useCollectionSubscriptionInTenant(db.collection('orderCounts').where('month', '>=', start).where('month', '<=', end), [start, end]);
  const prevYearOrderCounts = useCollectionSubscriptionInTenant(db.collection('orderCounts').where('month', '>=', addYears(start, -1)).where('month', '<=', addYears(end, -1)), [start, end]);
  const inventoryCounts = useCollectionSubscriptionInTenant(db.collection('inventoryCounts').where('month', '>=', start), [start]);
  const currentYearOrderCountsByMonth = keyBy(currentYearOrderCounts, _ => formatDate(_.month.toDate(), 'yyyyMM'));
  const prevYearOrderCountsByMonth = keyBy(prevYearOrderCounts, _ => formatDate(_.month.toDate(), 'yyyyMM'));
  const inventoryCountsByMonth = keyBy(inventoryCounts, _ => formatDate(_.month.toDate(), 'yyyyMM'));
  const productTypes = useCollectionSubscriptionInTenant(db.collection('productTypes').orderBy('index'));
  const productGroups = sortBy(useCollectionSubscriptionInTenant(db.collection('productGroups')), _ => _.createdAt.toDate());
  // オプション/パーツは商品種別を持たないためグループ固定
  const groupMode = !isBodyKind ? 'group' : (queryParams.groupMode === 'group' ? 'group' : 'productType');
  // グルーピング軸: productType別 or group別（group別は1商品が複数グループに属すると各グループに二重計上される）
  const groupingIdField = groupMode === 'group' ? 'productGroupIds' : 'productTypeIds';
  // どのグループ/種別にも属さない商品の受け皿
  const ungroupedGrouping = { id: ungroupedGroupingId, name: groupMode === 'group' ? '（グループ未選択）' : '（商品種別未選択）', };
  const groupings = [...(groupMode === 'group' ? productGroups : productTypes), ungroupedGrouping];
  const groupingOptions = groupings.map(_ => ({ label: _.name, value: _.id }));
  // 削除済みグループ/種別のIDが商品に残っていると行が消えるため、実在するIDのみを見る
  const existingGroupingIds = useMemo(_ => new Set((groupMode === 'group' ? productGroups : productTypes).map(_ => _.id)), [groupMode, productGroups, productTypes]);
  const groupingIdsOf = product => (product[groupingIdField] || []).filter(_ => existingGroupingIds.has(_));
  const tmpOrders = useCollectionSubscriptionInTenant(db.collectionGroup('tmpOrders').where('cammacsStatus', '==', 'initial'));
  const tmpOrderItems = tmpOrders.flatMap(_ => _.orderItems);
  const tmpOrderItemCounts = mapValues(groupBy(tmpOrderItems, 'productId'), _ => sumBy(_, 'quantity'));
  const preOrders = useCollectionSubscriptionInTenant(db.collection('orders').where('cammacsStatus', 'in', ['imported', 'initial']).where('isPreOrder', '==', true));
  const preOrderItems = preOrders.flatMap(_ => _.orderItems);
  const preOrderItemCounts = mapValues(groupBy(preOrderItems, 'productId'), _ => sumBy(_, 'quantity'));

  const preOrderCountsByMonth = useAsync(async () => {
    const monthlyData = await Promise.all(
      months.map(async (month) => {
        const productCounts = await Promise.all(
          targetProducts.map(async (product) => {
            const count = (await getCountFromServer(query(
              collection(db, 'orders'),
              where('tenantId', '==', tenant.id),
              where('isPreOrder', '==', true),
              where('productIds', 'array-contains', product.id),
              where('createdAt', '>=', month),
              where('createdAt', '<=', endOfMonth(month))
            ))).data().count;
            return [product.id, count];
          })
        );
        return [formatDate(month, 'yyyyMM'), fromPairs(productCounts)];
      })
    );
    return fromPairs(monthlyData);
  }, [tenant.id, months, targetProducts]);

  const commentsRef = db.collection('inventoryScheduleComments');
  const comments = orderBy(useCollectionSubscriptionInTenant(commentsRef), _ => _.createdAt.toDate(), 'desc');
  const rowGroups = groupings.map((grouping) => {
    const isUngrouped = grouping.id === ungroupedGroupingId;
    const rows = targetProducts.filter(_ => isUngrouped ? isEmpty(groupingIdsOf(_)) : groupingIdsOf(_).includes(grouping.id)).map((product) => {
      const abroadAlertInventory = product.abroadAlertInventory ?? 0;
      const expectedOrderCountMultiple = currentProspectPattern?.[`expectedOrderCountMultiple__${product.id}`];
      const tmpOrderCountValue = tmpOrderItemCounts[product.id] || 0;
      const preOrderCountValue = preOrderItemCounts[product.id] || 0;
      const benchmarkProductIds = uniq([product.id, ...(product.benchmarkProductIds || [])]);
      const monthColumns = months.map((month, monthIndex) => {
        const isPast = month < startOfMonth(new Date());
        const isCurrentMonth = formatDate(month, 'yyyyMM') === formatDate(new Date(), 'yyyyMM');
        const currentYearOrderCount = isCurrentMonth ? (
          { values: { [product.id]: sumBy(currentMonthOrderItemsByProductId[product.id], 'quantity') } }
        ) : currentYearOrderCountsByMonth[formatDate(month, 'yyyyMM')];
        const prevYearOrderCount = prevYearOrderCountsByMonth[formatDate(addYears(month, -1), 'yyyyMM')];
        const prevYearOrderCountValue = sumBy(benchmarkProductIds, _ => prevYearOrderCount?.values[_] ?? 0);
        const vsPrevYearOrderCount = [currentYearOrderCount, prevYearOrderCount].some(_ => _ == null) ? null : (currentYearOrderCount.values[product.id] ?? 0) - prevYearOrderCountValue;
        const inventoryCount = inventoryCountsByMonth[formatDate(month, 'yyyyMM')];
        const completedCount = computedPurchaseAndContainerCounts[product.id]?.completion[formatDate(month, 'yyyyMM')];
        const arrivalCount = computedPurchaseAndContainerCounts[product.id]?.arrival[formatDate(month, 'yyyyMM')] || 0;
        const prevMonthColumn = _ => monthColumns[monthIndex - 1];
        const manualExpectedOrderCount = inventoryCount?.values[`expectedOrderCount_${product.id}`];
        const column = {
          key: [product.id, formatDate(month, 'yyyyMM')].join('__'),
          manualExpectedOrderCount,
          month,
          isPast,
          currentYearOrderCount: _ => (isPast || isCurrentMonth) ? (currentYearOrderCount ? (currentYearOrderCount.values[product.id] ?? 0) : null) : null,
          predictedOrderCount: _ => {
            if (!isCurrentMonth) return null;
            const currentCount = column.currentYearOrderCount();
            if (currentCount == null) return null;
            const today = getDate(new Date());
            const daysInMonth = getDaysInMonth(new Date());
            return Math.round(currentCount * daysInMonth / today);
          },
          prevYearOrderCount: _ => prevYearOrderCount ? prevYearOrderCountValue : null,
          vsPrevYearOrderCount: _ => {
            if (isCurrentMonth) {
              const predicted = column.predictedOrderCount();
              if (predicted == null || prevYearOrderCount == null) return null;
              return predicted - prevYearOrderCountValue;
            }
            return isPast ? vsPrevYearOrderCount : null;
          },
          expectedOrderCount: _ => isPast ? null : (
            manualExpectedOrderCount ?? Math.round(column.prevYearOrderCount() * (expectedOrderCountMultiple ?? 1))
          ),
          tmpOrderCount: _ => isCurrentMonth ? tmpOrderCountValue : null,
          inventoryCount: _ => isPast ? (
            inventoryCount?.values[`inventory_${product.id}`] ?? null
          ) : prevMonthColumn() ? ((prevMonthColumn().inventoryCount() ?? 0) - column.expectedOrderCount() + column.arrivalCount() - column.tmpOrderCount()) : null,
          shouldAlertInventory: _ => !isPast && column.inventoryCount() < product.alertInventory,
          preOrderCount: _ => (isPast || isCurrentMonth) ? (preOrderCountsByMonth.value?.[formatDate(month, 'yyyyMM')]?.[product.id] ?? 0) : null,
          preOrderRemaining: _ => isPast ? null : isCurrentMonth ? preOrderCountValue : prevMonthColumn().preOrderRemaining() - Math.min(column.arrivalCount(), prevMonthColumn().preOrderRemaining()),
          completedCount: _ => completedCount,
          arrivalCount: _ => arrivalCount,
          transferingCount: _ => computedPurchaseAndContainerCounts[product.id]?.transferingCount(endOfMonth(month)),
          abroadInventoryCount: _ => computedPurchaseAndContainerCounts[product.id]?.abroadInventoryCount(endOfMonth(month)),
          totalInventoryCount: _ => {
            const inventory = column.inventoryCount();
            const abroad = column.abroadInventoryCount();
            return (inventory != null && abroad != null) ? inventory + abroad : null;
          },
          shouldAlertAbroadInventory: _ => !isPast && (_ => _?.abroadInventoryCount() <= abroadAlertInventory || column.abroadInventoryCount() <= abroadAlertInventory)(monthColumns.find(_ => formatDate(_.month, 'yyyyMM') === formatDate(addDays(endOfMonth(month), product.abroadInventoryLeadDays || 0), 'yyyyMM'))),
        };
        return column;
      });
      return {
        ...product,
        benchmarkProductIds,
        expectedOrderCountMultiple,
        rowType: 'product',
        monthColumns,
      };
    });
    let filteredRows = rows;
    if ((queryParams.showsHidden ?? '1') !== '1') {
      filteredRows = filteredRows.filter(_ => !_.isHidden);
    }
    if (queryParams.showsOut !== '1') {
      filteredRows = filteredRows.filter(_ => !_.isOut);
    }
    const summaryRow = {
      rowType: 'summary',
      ...grouping,
      normalInventory: filteredRows.every(_ => _.normalInventory == null) ? null : sumBy(filteredRows, _ => _.normalInventory ?? 0),
      monthColumns: months.map((month, monthIndex) => {
        const isPast = month < startOfMonth(new Date());
        const monthRows = filteredRows.map(_ => _.monthColumns[monthIndex]);

        // NOTE: ベンチマーク重複をなくす
        const prevYearOrderCount = prevYearOrderCountsByMonth[formatDate(addYears(month, -1), 'yyyyMM')];
        const prevYearOrderCountValue = sumBy(uniq(filteredRows.flatMap(_ => _.benchmarkProductIds)), _ => prevYearOrderCount?.values[_] ?? 0);

        const inventoryCount = inventoryCountsByMonth[formatDate(month, 'yyyyMM')];
        const column = {
          key: [grouping.id, formatDate(month, 'yyyyMM')].join('__'),
          month,
          isPast,
          ...fromPairs(
            [
              'currentYearOrderCount',
              'predictedOrderCount',
              'expectedOrderCount',
              'tmpOrderCount',
              'inventoryCount',
              'preOrderCount',
              'preOrderRemaining',
              'completedCount',
              'arrivalCount',
              'transferingCount',
              'abroadInventoryCount',
              'totalInventoryCount',
            ].map((metric) => [
              metric,
              () => monthRows.every(_ => _[metric]() == null) ? null : sumBy(monthRows, r => r[metric]())
            ])
          ),
          prevYearOrderCount: _ => prevYearOrderCount ? prevYearOrderCountValue : null,
          vsPrevYearOrderCount: _ => {
            if (monthRows.every(_ => _.vsPrevYearOrderCount() == null)) return null;
            const isCurrentMonth = formatDate(month, 'yyyyMM') === formatDate(new Date(), 'yyyyMM');
            const baseCount = isCurrentMonth
              ? sumBy(monthRows, r => r.predictedOrderCount())
              : sumBy(monthRows, r => r.currentYearOrderCount());
            return baseCount - prevYearOrderCountValue;
          },
          shouldAlertInventory: _ => !isPast && column.inventoryCount() < sumBy(rows, 'alertInventory'),
        };
        return column;
      }),
    };
    return {
      grouping,
      rows,
      filteredRows,
      summaryRow,
    };
  });

  let filteredRowGroups = rowGroups.filter(_ => _.filteredRows.length > 0);
  if (!isEmpty(queryParams.groupingIds)) {
    filteredRowGroups = filteredRowGroups.filter(_ => queryParams.groupingIds.includes(_.grouping.id));
  }
  if (queryParams.showsOnlySummary === '1') {
    filteredRowGroups = filteredRowGroups.map(_ => ({ ..._, filteredRows: _.filteredRows.filter(_ => _.rowType === 'summary'), }));
  }

  const filteredRows = filteredRowGroups.flatMap(_ => _.filteredRows);

  const isSelecting = selectedIds.length > 0;
  const isAllSelected = isEqual(...[selectedIds, filteredRows.map(_ => _.id)].map(_ => sortBy(_, _ => _)));
  const onSubmitBatchEdit = async (values, { onClickClose, }) => {
    await batch(db, selectedIds, (b, _) => {
      b.update(db.collection('products').doc(_), {
        ...values,
        updatedBy: omitBy(pick(firebaseUser, ['uid', 'email', 'displayName']), isUndefined),
      });
    });
    onClickClose();
  };
  useEffect(() => {
    if(queryParams.prospectPatternId == null && prospectPatterns.length > 0) {
      history.replace(location.pathname + '?' + qs.stringify({ ...queryParams, prospectPatternId: prospectPatterns[0]?.id }));
    }
  }, [prospectPatterns]);

  return (
    <div>
      <div className="admin-inventories-schedule container-fluid py-5 position-relative bg-white">
        <InventoriesHeader activeTab="schedule" />
        <div className="d-md-flex">
          <div className="flex-grow-1" style={{ width: window.innerWidth >= 768 ? 'calc(85vw - 30px)' : '100vw', maxWidth: window.innerWidth >= 768 ? 'calc(100vw - 210px)' : '100vw', }}>
            <div className="mt-4 d-flex justify-content-start align-items-end gap-1 mb-1 position-relative" style={{ zIndex: 101 }}>
              {/* 予想販売係数パターンは本体商品のみが対象 */}
              <QuerySelector
                paramName='prospectPatternId'
                width={250}
                options={prospectPatternOptions}
                label='予想販売係数パターン'
                isClearable={false}
                disabled={!isBodyKind}
              />
              <ModalButton title="予想販売係数パターン" content={_ => <ProspectPatterns {...{ tenant, user, products: bodyProducts, }} />} modalProps={{ style: { minWidth: 700 } }} disabled={!isBodyKind}>
                予想販売係数パターン管理
              </ModalButton>
              <QueryButtonGroup
                paramName='productKind'
                defaultValue='body'
                options={productKindOptions}
                disabled={isSelecting}
              />
              {/* オプション/パーツは商品種別を持たないためグループ固定 */}
              <QueryButtonGroup
                paramName='groupMode'
                defaultValue='productType'
                options={[{ label: '商品種別', value: 'productType' }, { label: 'グループ', value: 'group' }]}
                disabled={isSelecting || !isBodyKind}
                forcedValue={!isBodyKind ? 'group' : undefined}
              />
            </div>
            <div className="mt-4 d-flex flex-wrap justify-content-start align-items-end gap-1 mb-1 position-relative" style={{ zIndex: 100 }}>
              <QueryDateRangeSelector
                label="期間"
                defaultValue={[start, end]}
                paramName="dateRange"
                pickerProps={{ showMonthYearPicker: true, }}
              />
              <QuerySelector
                paramName='groupingIds'
                width={250}
                isMulti
                options={groupingOptions}
                label={groupMode === 'group' ? 'グループで絞込み' : '商品種別で絞込み'}
                disabled={isSelecting}
              />
              <QuerySelector
                paramName='metrics'
                width={200}
                isMulti
                options={metricOptions}
                defaultValue={defaultMetricNames}
                label='表示項目'
                selectorProps={{
                  components: {
                    ValueContainer: ({ children, getValue, options: opts, ...props }) => {
                      const selected = getValue();
                      const total = opts.length;
                      return (
                        <reactSelectComponents.ValueContainer {...props} getValue={getValue} options={opts}>
                          <span className="text-muted">({selected.length}/{total} 表示中)</span>
                          {last(children)}
                        </reactSelectComponents.ValueContainer>
                      );
                    },
                    ClearIndicator: () => null,
                    IndicatorSeparator: () => null,
                  },
                  closeMenuOnSelect: false,
                  hideSelectedOptions: false,
                }}
              />
              <QueryBoolean paramName='isActual' label='実績ベース' defaultValue='0' disabled={isSelecting} />
              <QueryBoolean paramName='showsOnlySummary' label={groupMode === 'group' ? 'グループ単位のみ' : '商品種別単位のみ'} defaultValue='0' disabled={isSelecting} />
              <QueryBoolean paramName='showsHidden' label='非表示も表示' defaultValue='1' disabled={isSelecting} />
              <QueryBoolean paramName='showsOut' label='廃盤も表示' defaultValue='0' disabled={isSelecting} />
              <SavedSearchParameterForm {...props} type="inventorySchedule" />
            </div>
            {
              selectedIds.length > 0 && (
                <div className="d-flex mb-2 alert alert-info justify-content-between align-items-center">
                  <div>{selectedIds.length}件を選択中</div>
                  {/* ベンチマーク商品は本体商品のみが対象 */}
                  {/* 商品マスタはシミュレーション対象外。ロック中も変更させない */}
                  <ModalButton color="secondary" Modal={ModelFormModal} modalProps={{ title: '編集', fields: prospectFields({ products: bodyProducts, }), onSubmit: onSubmitBatchEdit, }} disabled={!isBodyKind || isSimulating || isMasterLocked} title={isSimulating ? '商品マスタはシミュレーション対象外です' : undefined}>
                    編集
                  </ModalButton>
                </div>
              )
            }
            <div className="overflow-auto" style={{ maxHeight: '90vh', zIndex: 0, }}>
              <table className="table table-bordered sticky-table m-0 bg-white">
                <thead className="bg-light-grey text-center">
                  <tr>
                    <th style={{ minWidth: 50 }} className="sticky bg-light-grey">
                      <Input
                        type='checkbox'
                        className='d-none position-relative m-0'
                        checked={isAllSelected}
                        onChange={_ => isAllSelected ? setSelectedId([]) : setSelectedId(filteredRows.map(_ => _.id))}
                      />
                    </th>
                    <th style={{ minWidth: 300, left: 50, }} className="sticky bg-light-grey">
                    </th>
                    <th style={{ minWidth: 150, left: 350, }} className="sticky bg-light-grey">
                      指標
                    </th>
                    {
                      months.map((month) => {
                        const isPast = month < startOfMonth(new Date());
                        const isCurrentMonth = formatDate(month, 'yyyyMM') === formatDate(new Date(), 'yyyyMM');
                        const onClickSaveOrderCounts = async () => {
                          try {
                            await Promise.all([0, -1].map(_ => saveOrderCounts({ tenantId: tenant.id, monthString: addYears(month, _).toISOString(), })));
                          } catch(e) {
                            toast.error('失敗しました');
                          }
                        };

                        return (
                          <Fragment key={month.toISOString()}>
                          {
                            isCurrentMonth && (
                              <th style={{ minWidth: 100 }}>
                                現在
                              </th>
                            )
                          }
                          <th style={{ minWidth: 150 }} className="text-nowrap">
                            {formatDate(month, 'yyyy/MM')}末
                            {/* 注文数の実績集計はマスタのみが対象 */}
                            {
                              !isSimulating && (
                                <ProgressButton color="link" size="sm" className="p-1 text-primary" process={onClickSaveOrderCounts} disabled={isMasterLocked}>
                                  <span className="fas fa-calculator" />
                                </ProgressButton>
                              )
                            }
                            <EditButton
                              color="link"
                              size="sm"
                              label={false}
                              className="p-1"
                              disabled={isSimulating || isMasterLocked}
                              itemRef={db.collection('inventoryCounts').doc([tenant.id, formatDate(month, 'yyyyMM')].join('__'))}
                              FormModal={ModelFormModal}
                              formProps={{
                                title: '編集',
                                fields: {
                                  ...(
                                    isPast && fromPairs([
                                    ...filteredRows.map(_ => [`inventory_${_.id}`, { label: `在庫 ${_.id}`, type: 'integer', }]),
                                    ])
                                  ),
                                  ...(
                                    !isPast && fromPairs([
                                    ...filteredRows.map(_ => [`expectedOrderCount_${_.id}`, { label: `予想販売数 ${_.id}`, type: 'integer', }]),
                                    ])
                                  ),
                                },
                              }}
                              beforeEdit={_ => _?.values}
                              beforeSubmit={_ => ({
                                tenantId: tenant.id,
                                month,
                                savedAt: new Date(),
                                values: _,
                              })}
                              type="set"
                            />
                          </th>
                          </Fragment>
                        );
                      })
                    }
                  </tr>
                </thead>
                {
                  filteredRowGroups.map((rowGroup) => {
                    const { grouping, } = rowGroup;
                    return (
                      <RowGroup key={grouping.id} {...{ ...rowGroup, groupMode, isBodyKind, tenant, user, bodyProducts, selectedIds, unselectId, selectId, cellNotesById, }} />
                    );
                  })
                }
              </table>
            </div>
          </div>
          <div style={{ width: window.innerWidth >= 768 ? '15vw' : '100%', minWidth: '180px' }} className="mt-3 mt-md-0 pl-md-3">
            <div className="sticky-top" style={{ top: 50 }}>
              <h5 className="mb-3">コメント</h5>
              <div className='d-flex justify-content-end mb-3'>
                <AddInTenantButton
                  label='新規コメント'
                  itemRef={commentsRef.doc()}
                  processValues={(_) => ({ ..._, createdBy: pick(user, ['uid', 'email', 'displayName']) })}
                  FormModal={ModelFormModal}
                  formProps={{ title: '新規コメント', fields: commentFields }}
                />
              </div>
              <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
                {comments.length > 0 ? (
                  <div>
                    {comments.map(({ id, body, createdAt, createdBy }) => {
                      return (
                        <div key={id} className='card p-3 mb-3'>
                          <div className='small text-muted mb-1'>
                            {createdBy.displayName} {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                          </div>
                          <div className="text-break" style={{ whiteSpace: 'pre-line' }}>{body}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>コメントはまだありません</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});


function RowGroup (props) {
  const { tenant, user, groupMode, isBodyKind, bodyProducts, summaryRow, filteredRows, selectedIds, unselectId, selectId, cellNotesById, } = props;

  return (
    <tbody>
      {
        [summaryRow, ...filteredRows].map((row) => {
          const { id, ref, name, expectedOrderCountMultiple = 1, monthColumns, } = row;
          return (
            <Row key={id} {...{ tenant, user, groupMode, isBodyKind, row, selectedIds, unselectId, selectId, bodyProducts, cellNotesById, }} />
          );
        })
      }
    </tbody>
  );
}

function Row (props) {
  const { tenant, user, groupMode, isBodyKind, row, bodyProducts, selectedIds, unselectId, selectId, cellNotesById, } = props;
  const { id, ref, rowType, name, expectedOrderCountMultiple = 1, benchmarkProductIds, monthColumns, normalInventory, } = row;
  const queryParams = useQueryParams();
  const displayMetrics = queryParams.metrics ? keys(pickBy(metrics, (v, k) => queryParams.metrics.includes(k))) : defaultMetricNames;

  return (
    <tr className={classnames({ 'font-weight-bold': rowType === 'summary' })}>
      {
        rowType === 'product' ? (
          <Fragment>
            <th style={{ minWidth: 50 }} className="sticky bg-light-grey">
              <Input
                type='checkbox'
                className='d-none position-relative m-0'
                checked={selectedIds.includes(id)}
                onChange={(_) => (selectedIds.includes(id) ? unselectId(id) : selectId(id))}
              />
            </th>
            <th style={{ minWidth: 300, left: 50, }} className="sticky bg-light-grey">
              <div>
                <div>
                  {id}
                </div>
                <div>
                  {name}
                </div>
                {/* 予想販売係数・ベンチマーク商品は本体商品のみが対象 */}
                <div className={classnames('d-flex gap-1 mt-3', { 'text-muted': !isBodyKind })}>
                  <div className="d-flex gap-1 flex-wrap align-items-center">
                    <span className={classnames('badge', isBodyKind ? 'badge-secondary' : 'badge-light')}>
                      {isBodyKind ? numeral(expectedOrderCountMultiple).format('0,0%') : '-'}
                    </span>
                    {
                      isBodyKind && benchmarkProductIds?.length > 0 && (
                        <div className="card p-1">
                          <div className="d-flex gap-1">
                            <div className="small">
                              BM:
                            </div>
                            {
                              benchmarkProductIds.map(_ => (
                                <span className="badge badge-secondary" key={_}>
                                  {_}
                                </span>
                              ))
                            }
                          </div>
                        </div>
                      )
                    }
                  </div>
                  <EditButton
                    color="link"
                    size="sm"
                    label={false}
                    className="p-1"
                    itemRef={ref}
                    FormModal={ModelFormModal}
                    formProps={{ documentName: 'product', title: '商品編集', fields: prospectFields({ products: bodyProducts, }), }}
                    disabled={!isBodyKind}
                  />
                </div>
              </div>
            </th>
          </Fragment>
        ) : (
          <th colSpan={2} style={{ minWidth: 350, }} className="sticky bg-light-grey">
            {name}
          </th>
        )
      }
      <th style={{ minWidth: 130, left: 350, }} className="sticky bg-light-grey">
        <div className="d-flex flex-column align-items-stretch">
          {
            entries(pick(metrics, displayMetrics)).map(([metric, { label }]) => {
              return (
                <div key={metric} className="text-right">
                  {label}
                </div>
              );
            })
          }
        </div>
      </th>
      {
        monthColumns.map((monthColumn, monthIndex) => {
          const { month, key, manualExpectedOrderCount, } = monthColumn;
          const cell = (n, isComparison, rate, to, className, style, key) => {
            const Component = to == null ? 'div' : TenantLink;
            return (
              <Component key={key} className={classnames('text-right d-flex justify-content-end align-items-center', className)} to={to} style={style}>
                {
                  n != null ? (
                    <Fragment>
                      <div>{(isComparison && n > 0 ? '+' : '') + numeral(n).format()}</div>
                      {rate != null && <div className="small">({isFinite(rate) ? numeral(rate).format('+0,0.0%') : ' '})</div>}
                    </Fragment>
                  ) : '-'
                }
              </Component>
            );
          };
          const hasNote = !isEmpty(cellNotesById[key]?.body);
          const isCurrentMonth = formatDate(month, 'yyyyMM') === formatDate(new Date(), 'yyyyMM');

          return (
            <Fragment key={monthIndex}>
            {
              isCurrentMonth && (
                <td className="position-relative">
                  <div className="d-flex flex-column align-items-stretch">
                    {
                      entries(pick(metrics, displayMetrics)).map(([metric]) => (
                        <div key={metric} className="text-right d-flex justify-content-end align-items-center">
                          {metric === 'inventoryCount' && normalInventory != null ? numeral(normalInventory).format() : '-'}
                        </div>
                      ))
                    }
                  </div>
                </td>
              )
            }
            <td className="position-relative has-hovered-contents">
              <AdminHelp
                collectionName="cellNotes"
                helpKey={key}
                user={user}
                title="メモ"
                label={false}
                disabledPreview
                buttonProps={{
                  className: classnames('position-absolute p-0', { 'hovered-content': !hasNote }),
                  style: { top: 1, left: 2, },
                }}
                icon="sticky-note"
              />
              <div className="d-flex flex-column align-items-stretch">
                {
                  entries(pick(metrics, displayMetrics)).map(([metric, { isComparison = _ => false, rate, path, className, style, }]) => {
                    const value = monthColumn[metric]();
                    const computedClassName = [
                      (className ? className(monthColumn) : value < 0 && 'text-danger'),
                      metric === 'expectedOrderCount' && manualExpectedOrderCount != null && 'text-success',
                    ].join(' ');
                    const computedStyle = style?.(monthColumn);
                    return cell(value, isComparison(), rate?.(monthColumn), path?.({ type: rowType, groupMode, id, month, }), computedClassName, computedStyle, metric);
                  })
                }
              </div>
            </td>
            </Fragment>
          );
        })
      }
    </tr>
  );
}

function ProspectPatterns (props) {
  const { user, tenant, products, } = props;
  const prospectPatterns = sortBy(useCollectionSubscriptionInTenant(db.collection('prospectPatterns'), []), _ => _.createdAt.toDate());
  const prospectPatternFields = {
    name: {
      label: '名称',
      type: 'string',
      validations: { required: _ => !isEmpty(_), },
    },
    memo: {
      label: 'メモ',
      type: 'text',
    },
    ...fromPairs(products.map((product) => {
      return [
        `expectedOrderCountMultiple__${product.id}`,
        {
          label: `予想販売係数 ${product.id}`,
          type: 'float',
          initialValue: 1,
        }
      ];
    })),
  };

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <AddInTenantButton itemRef={db.collection('prospectPatterns').doc()} FormModal={ModelFormModal} formProps={{ documentName: 'prospectPattern', title: '予想販売係数パターン追加', fields: prospectPatternFields }} disabled={!canUpdateProspectPattern(user)} />
      </div>
      <table className="table">
        <thead className="thead-light text-center">
          <tr>
            <th style={{ minWidth: 200 }}>名称</th>
            <th style={{ minWidth: 200 }}>メモ</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {
            prospectPatterns.map((prospectPattern) => {
              const { id, ref, name, text, } = prospectPattern;
              return (
                <tr key={id}>
                  <td>
                    {name}
                  </td>
                  <td style={{ whiteSpace: 'pre-line' }}>
                    {prospectPattern.memo}
                  </td>
                  <td className="text-nowrap text-right">
                    <EditButton itemRef={ref} FormModal={ModelFormModal} formProps={{ documentName: 'prospectPattern', title: '予想販売係数パターン編集', fields: prospectPatternFields, }} disabled={!canUpdateProspectPattern(user)} />
                    <DeleteButton itemRef={ref} className="ml-1" disabled={!canUpdateProspectPattern(user)} />
                  </td>
                </tr>
              );
            })
          }
        </tbody>
      </table>
    </div>
  );
}
